type ConsentEvent = CustomEvent<{ data: string[] }>;

export type HandleConsent = (event: ConsentEvent) => boolean | void;

interface Tag<S extends string> {
  readonly __tag: S;
}

export type RichRelevanceConsent = boolean & Tag<"RichRelevance">;

declare global {
  interface Window {
    ebConsentQueue?: HandleConsent[];
  }
}
export interface RecoConsentInterface {
  setupQueue(): void;
  getRichRelevanceConsent(): Promise<RichRelevanceConsent>;
}
export const getRecoConsent = (
  storage: Storage,
  ebConsentQueue: HandleConsent[],
) => {
  return class RecoConsent implements RecoConsentInterface {
    private static RICH_RELEVANCE_SESSION_STORAGE_KEY: "reco_recommendations_frontend_consent" =
      "reco_recommendations_frontend_consent" as const;
    private static RICH_RELEVANCE_KEY: "RICHR" = "RICHR" as const;
    private resolveConsentPresent!: (
      value: RichRelevanceConsent | PromiseLike<RichRelevanceConsent>,
    ) => void;
    private readonly consentPresent: Promise<RichRelevanceConsent>;
    private skipConsentQueueTimeout: NodeJS.Timeout | undefined;
    private consentStorage: Storage;

    constructor() {
      this.consentStorage = storage;
      this.consentPresent = new Promise<RichRelevanceConsent>((resolve) => {
        this.resolveConsentPresent = resolve;
      });
    }
    setupQueue(): void {
      ebConsentQueue.push(this.handleConsentEvent.bind(this));
      this.skipConsentQueueTimeout = setTimeout(() => {
        this.resolveConsentPresentAndClearTimeout(
          false as RichRelevanceConsent,
        );
      }, 2000);
    }

    getRichRelevanceConsent(): Promise<RichRelevanceConsent> {
      try {
        const storedConsent = this.consentStorage.getItem(
          RecoConsent.RICH_RELEVANCE_SESSION_STORAGE_KEY,
        );
        if (storedConsent !== null) {
          this.resolveConsentPresentAndClearTimeout(
            (storedConsent === "true") as RichRelevanceConsent,
          );
        }
        // eslint-disable-next-line sonarjs/no-ignored-exceptions
      } catch (_) {
        this.resolveConsentPresentAndClearTimeout(
          false as RichRelevanceConsent,
        );
      }
      return this.consentPresent;
    }

    private storeConsentInSessionStorage(
      consentGiven: RichRelevanceConsent,
    ): void {
      try {
        this.consentStorage.setItem(
          RecoConsent.RICH_RELEVANCE_SESSION_STORAGE_KEY,
          `${consentGiven}`,
        );
        // eslint-disable-next-line no-empty
      } catch (_) {}
    }

    private handleConsentEvent(event: ConsentEvent): void {
      const consentGiven = event.detail.data.includes(
        RecoConsent.RICH_RELEVANCE_KEY,
      ) as RichRelevanceConsent;
      this.storeConsentInSessionStorage(consentGiven);
      this.resolveConsentPresentAndClearTimeout(consentGiven);
    }

    private resolveConsentPresentAndClearTimeout(
      consent: RichRelevanceConsent,
    ): void {
      if (this.skipConsentQueueTimeout) {
        clearTimeout(this.skipConsentQueueTimeout);
        this.skipConsentQueueTimeout = undefined;
      }
      this.resolveConsentPresent(consent);
    }
  };
};
